import React, { useRef } from 'react';
import {
  ListBase,
  ListToolbar,
  Pagination,
  useRefresh,
  Title,
  Datagrid,
  FunctionField,
} from 'react-admin';

import { Card, makeStyles } from '@material-ui/core';
import AddShoppingCartIcon from '@material-ui/icons/AddShoppingCart';
import EditIcon from '@material-ui/icons/Edit';
import PrintIcon from '@material-ui/icons/Print';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import { useSnackbar } from 'notistack';

import { DialogoConfirmacao } from '../../Components';
import ActionsField from '../../Components/ReactAdmin/Fields/ActionsField';
import Textofield from '../../Components/ReactAdmin/Fields/TextoField';
import { ProtegerPagina } from '../../layout/VerificaPermissaoPage';
import { getListAllAPI, getListAPI, updateAPI } from '../../services';
import { PERMISSAO_TELA_AGENDAMENTO_COMPRAS_FORNECEDORES_LISTAR } from '../../utils/dadosTelas';
import DialogoPDF from '../PedidosCompras/DialogoPDF';
import DialogoPedidoCompra from '../PedidosCompras/DialogoPedidoCompra';
import ListActions from './ActionsList';
import DialogoEditarDataAgendamento from './DialogoEditarDataAgendamento';
import Filter from './Filter';
import StatusDataField from './StatusDataField';

function getDiaSemana(data, utc) {
  return data.toLocaleDateString('pt-BR', {
    weekday: 'long',
    ...(utc ? { timeZone: 'UTC' } : {}),
  });
}

function formatarProximaData(value) {
  if (!value) return '-';
  const data = new Date(value);
  const dataFormatada = data.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  const diaSemana = getDiaSemana(data, true);
  return `${dataFormatada} (${diaSemana})`;
}

function formatarUltimaConfirmacao(value) {
  if (!value) return '-';
  const data = new Date(value);
  const dataFormatada = data.toLocaleDateString('pt-BR');
  const horaFormatada = data.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const diaSemana = getDiaSemana(data, false);
  return `${dataFormatada} ${horaFormatada} (${diaSemana})`;
}

const useStyles = makeStyles((theme) => ({
  dataHeader: {
    flex: 1,
    backgroundColor: theme.palette.secondary.main,
    padding: '10px',
    color: 'white',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active': {
      color: 'red !important',
      fontWeight: 'bold',
    },
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
      color: 'red !important',
      fontWeight: 'bold',
    },
  },
  dataCell: {
    flex: 1,
    padding: '10px',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
  },
}));

const MyList = ({ ...props }) => {
  const classes = useStyles();
  const refresh = useRefresh();
  const { enqueueSnackbar } = useSnackbar();
  const refDialogoPedidoCompra = useRef(null);
  const refDialogoEditarData = useRef(null);
  const refDialogoPDF = useRef(null);
  const refDialogoConfirmacaoImprimir = useRef(null);

  function handleEditarData({ id, data_agendamento }) {
    if (refDialogoEditarData.current) {
      refDialogoEditarData.current.handleOpen(id, data_agendamento);
    }
  }

  async function handleSalvarData(id, data_agendamento) {
    try {
      await updateAPI('agendamento-compra-fornecedores', id, {
        data_agendamento,
      });
      refresh();
    } catch (erros) {
      const mensagemErro =
        erros.response && erros.response.data
          ? erros.response.data.message || erros.response.data
          : `${erros}`;
      enqueueSnackbar(mensagemErro, { variant: 'error' });
    }
  }

  async function handleNovoPedido({
    fornecedor_id,
    pasta_fornecedor_id,
    nome_pasta,
  }) {
    const data = await getListAllAPI(
      'fornecedores',
      ['id', 'asc'],
      { id: fornecedor_id },
      ['cidade.estado'],
    );
    const fornecedor = data.data[0];
    const pasta = pasta_fornecedor_id
      ? { id: pasta_fornecedor_id, nome: nome_pasta }
      : null;

    if (refDialogoPedidoCompra.current) {
      refDialogoPedidoCompra.current.handleCreate(fornecedor, pasta);
    }
  }

  async function handleEditarUltimoPedido({
    fornecedor_id,
    pasta_fornecedor_id,
  }) {
    const { data } = await getListAPI(
      'pedidos-compras',
      ['data', 'desc'],
      [1, 1],
      { fornecedor_id, pasta_fornecedor_id },
      [],
    );

    if (data.length === 0) {
      enqueueSnackbar(
        'Este agendamento ainda não tem nenhum pedido de compra registrado!',
        { variant: 'info' },
      );
      return;
    }

    if (refDialogoPedidoCompra.current) {
      refDialogoPedidoCompra.current.handleUpdate(data[0].id);
    }
  }

  async function handleImprimirUltimoPedido({
    fornecedor_id,
    pasta_fornecedor_id,
  }) {
    const { data } = await getListAPI(
      'pedidos-compras',
      ['data', 'desc'],
      [1, 1],
      { fornecedor_id, pasta_fornecedor_id },
      [],
    );

    if (data.length === 0) {
      enqueueSnackbar(
        'Este agendamento ainda não tem nenhum pedido de compra registrado!',
        { variant: 'info' },
      );
      return;
    }

    if (refDialogoPDF.current) {
      refDialogoPDF.current.handleOpen(data[0].id);
    }
  }

  return (
    <>
      <Title title="Agendamento de Compras" />
      <ListToolbar
        filters={props.filters}
        actions={<ListActions />}
        {...props}
      />
      <Card style={{ borderRadius: '20px' }} {...props}>
        <Datagrid {...props}>
          <Textofield
            source="identificacao_fornecedor"
            label="Fornecedor"
            headerClassName={classes.dataHeader}
            cellClassName={classes.dataCell}
          />
          <Textofield
            source="nome_pasta"
            label="Pasta"
            textoPadrao="-"
            headerClassName={classes.dataHeader}
            cellClassName={classes.dataCell}
          />
          <FunctionField
            source="data_agendamento"
            label="Próxima Data de Compra"
            render={(record) => formatarProximaData(record.data_agendamento)}
            headerClassName={classes.dataHeader}
            cellClassName={classes.dataCell}
          />
          <StatusDataField
            source="data_agendamento"
            label="Situação"
            sortable={false}
            headerClassName={classes.dataHeader}
            cellClassName={classes.dataCell}
          />
          <FunctionField
            source="data_ultima_confirmacao"
            label="Última Confirmação"
            render={(record) =>
              formatarUltimaConfirmacao(record.data_ultima_confirmacao)
            }
            headerClassName={classes.dataHeader}
            cellClassName={classes.dataCell}
          />
          <ActionsField
            source="id"
            label="Ações"
            sortable={false}
            headerClassName={classes.dataHeader}
            cellClassName={classes.dataCell}
            actions={[
              {
                title: 'Editar Próxima Data de Compra',
                icon: <EditIcon />,
                show: () => true,
                onClick: (record) => handleEditarData(record),
              },
              {
                title: 'Novo Pedido de Compra',
                icon: <AddShoppingCartIcon />,
                show: () => true,
                onClick: (record) => handleNovoPedido(record),
              },
              {
                title: 'Editar Último Pedido de Compra',
                icon: <ShoppingCartIcon />,
                show: () => true,
                onClick: (record) => handleEditarUltimoPedido(record),
              },
              {
                title: 'Imprimir Último Pedido',
                icon: <PrintIcon />,
                show: () => true,
                onClick: (record) => handleImprimirUltimoPedido(record),
              },
            ]}
          />
        </Datagrid>
        <AgendamentoPagination />
      </Card>
      <DialogoPedidoCompra
        ref={refDialogoPedidoCompra}
        handleClose={(idCriado) => {
          refresh();
          if (idCriado) {
            refDialogoConfirmacaoImprimir.current.handleOpen(
              'Pedido salvo',
              'Pedido de compra salvo com sucesso! Deseja imprimir agora?',
              idCriado,
            );
          }
        }}
      />
      <DialogoEditarDataAgendamento
        ref={refDialogoEditarData}
        handleSalvar={handleSalvarData}
      />
      <DialogoPDF ref={refDialogoPDF} />
      <DialogoConfirmacao
        ref={refDialogoConfirmacaoImprimir}
        handleConfirma={(idCriado) =>
          refDialogoPDF.current.handleOpen(idCriado)
        }
      />
    </>
  );
};

const ContainerMyList = ({ ...props }) => (
  <ListBase {...props}>
    <MyList {...props} />
  </ListBase>
);

const AgendamentoPagination = (props) => (
  <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />
);

const List2 = (props) => (
  <ProtegerPagina
    permissao={PERMISSAO_TELA_AGENDAMENTO_COMPRAS_FORNECEDORES_LISTAR}
  >
    <ContainerMyList
      {...props}
      bulkActionButtons={false}
      pagination={<AgendamentoPagination />}
      perPage={50}
      filters={<Filter />}
      component="div"
      filterDefaultValues={{ ativo: true }}
      sort={{ field: 'data_agendamento', order: 'ASC' }}
    />
  </ProtegerPagina>
);

export default List2;
